import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getBlueprint, forkBlueprint, postShopResult, purchaseBlueprint, saveBlueprintApi, unsaveBlueprintApi } from '../services/api';
import { useCustomerAuth } from '../services/CustomerAuthContext';
import { useAuth } from '../services/AuthContext';

const BRANCH_COLORS = ['#4a7c59','#5b8dd9','#ab47bc','#e67e22','#1abc9c','#e74c3c','#f39c12','#9b59b6'];

function FamilyTree({ versions, variations, currentBpId, navigate }) {
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [tooltip, setTooltip] = useState(null);
  const viewportRef = useRef(null);

  const onMouseDown = (e) => { setDragging(true); setDragStart({ x: e.clientX - panX, y: e.clientY - panY }); };
  const onMouseMove = (e) => { if (!dragging) return; setPanX(e.clientX - dragStart.x); setPanY(e.clientY - dragStart.y); };
  const onMouseUp = () => setDragging(false);
  const onWheel = useCallback((e) => { e.preventDefault(); setZoom(z => Math.max(0.3, Math.min(3, z + (e.deltaY > 0 ? -0.1 : 0.1)))); }, []);
  const resetView = () => { setPanX(0); setPanY(0); setZoom(1); };

  useEffect(() => {
    const el = viewportRef.current;
    if (el) el.addEventListener('wheel', onWheel, { passive: false });
    return () => { if (el) el.removeEventListener('wheel', onWheel); };
  }, [onWheel]);

  const sortedVersions = [...versions].sort((a, b) => (a.publishedAt || 0) - (b.publishedAt || 0));
  const sortedVariations = [...variations].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

  // Each fork gets its own branch color and lane
  const getColor = (idx) => BRANCH_COLORS[idx % BRANCH_COLORS.length];
  const getLane = (idx) => 50 + idx * 30;

  const rowH = 70;
  const mainX = 15;
  const mainH = sortedVersions.length * rowH;
  const totalH = mainH + sortedVariations.length * rowH + 100;

  return (
    <div className="family-tree-viewport" ref={viewportRef} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
      <div className="family-tree-controls">
        <button onClick={() => setZoom(z => Math.min(3, z + 0.2))}>+</button>
        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))}>−</button>
        <button onClick={resetView}>⟲</button>
      </div>
      <div className="family-tree-canvas" style={{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})`, transformOrigin: '0 0' }}>
        <svg width={Math.max(500, 100 + sortedVariations.length * 30)} height={totalH}>
          {/* Mainline */}
          <line x1={mainX} y1={15} x2={mainX} y2={mainH + 15} stroke="#c0392b" strokeWidth="3"/>
          {sortedVersions.map((v, i) => (
            <circle key={`m${i}`} cx={mainX} cy={30 + i * rowH} r={7} fill="#c0392b" style={{ cursor: 'default' }}
              onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, title: `v${v.version}`, sub: v.changelog, date: v.publishedAt })}
              onMouseLeave={() => setTooltip(null)} />
          ))}
          {/* Each fork = its own branch */}
          {sortedVariations.map((v, idx) => {
            const lane = getLane(idx);
            const color = getColor(idx);
            const vy = mainH + 30 + idx * rowH;
            // Determine where this branch originates from
            let branchFromX = mainX;
            let branchFromY;
            const parentForkIdx = sortedVariations.findIndex(sv => sv.blueprintId === v.forkedFrom);
            if (parentForkIdx >= 0) {
              // Fork-of-fork: branch from the parent fork's node
              branchFromX = getLane(parentForkIdx);
              branchFromY = mainH + 30 + parentForkIdx * rowH;
            } else {
              // Direct fork from mainline: branch from the version node
              const forkedVersion = v.forkedFromVersion;
              const mainIdx = sortedVersions.findIndex(mv => mv.version === forkedVersion);
              branchFromY = 30 + (mainIdx >= 0 ? mainIdx : sortedVersions.length - 1) * rowH;
            }
            const isActive = v.blueprintId === currentBpId;
            return (
              <g key={`branch-${idx}`}>
                <path d={`M ${branchFromX} ${branchFromY} C ${branchFromX} ${branchFromY + (vy - branchFromY) * 0.5}, ${lane} ${vy - (vy - branchFromY) * 0.3}, ${lane} ${vy}`}
                  stroke={color} strokeWidth="2.5" fill="none" opacity="0.8"/>
                <circle cx={lane} cy={vy} r={6} fill={color}
                  stroke={isActive ? '#fff' : 'none'} strokeWidth={isActive ? 2 : 0}
                  style={{ cursor: 'pointer', filter: isActive ? `drop-shadow(0 0 4px ${color})` : 'none' }}
                  onClick={() => navigate(`/workshop/blueprints/${v.blueprintId}`)}
                  onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, title: v.title, sub: `by ${v.creatorName || v.creatorId}`, date: v.createdAt, extra: `Forked from v${v.forkedFromVersion}` })}
                  onMouseLeave={() => setTooltip(null)} />
              </g>
            );
          })}
        </svg>
        {/* Text labels */}
        <div style={{ position: 'absolute', top: 0, left: 0 }}>
          {sortedVersions.map((v, i) => (
            <div key={`t${i}`} style={{ position: 'absolute', top: `${18 + i * rowH}px`, left: '2.5rem', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>v{v.version}</span>
              <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem', color: 'var(--text-muted)' }}>{v.changelog}</span>
            </div>
          ))}
          {sortedVariations.map((v, i) => (
            <div key={`vt${i}`} style={{ position: 'absolute', top: `${mainH + 18 + i * rowH}px`, left: `${getLane(i) + 15}px`, whiteSpace: 'nowrap' }}>
              <Link to={`/workshop/blueprints/${v.blueprintId}`} style={{ textDecoration: 'none', color: 'inherit', fontSize: '0.82rem', fontWeight: 500 }}>
                {v.title}
              </Link>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>by {v.creatorName || v.creatorId}</div>
            </div>
          ))}
        </div>
      </div>
      {tooltip && (
        <div className="family-tree-tooltip" style={{ left: tooltip.x + 12, top: tooltip.y - 40, position: 'fixed' }}>
          <strong>{tooltip.title}</strong>
          {tooltip.sub && <div>{tooltip.sub}</div>}
          {tooltip.extra && <div style={{ fontStyle: 'italic' }}>{tooltip.extra}</div>}
          {tooltip.date && <div>{new Date((tooltip.date || 0) * 1000).toLocaleDateString()}</div>}
        </div>
      )}
    </div>
  );
}

function BlueprintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  const { vendor } = useAuth();
  const [bp, setBp] = useState(null);
  const [versions, setVersions] = useState([]);
  const [mainlineVersions, setMainlineVersions] = useState([]);
  const [variations, setVariations] = useState([]);
  const [shopResults, setShopResults] = useState([]);
  const [activeTab, setActiveTab] = useState('cutlist');
  const [loading, setLoading] = useState(true);
  const [showFork, setShowFork] = useState(false);
  const [forkTitle, setForkTitle] = useState('');
  const [forkBasedOn, setForkBasedOn] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [resultForm, setResultForm] = useState({ displayName: '', notes: '' });
  const [saved, setSaved] = useState(false);
  const [starCount, setStarCount] = useState(0);

  useEffect(() => {
    getBlueprint(id).then(data => {
      setBp(data.blueprint);
      setVersions(data.versions || []);
      setMainlineVersions(data.mainlineVersions || data.versions || []);
      setVariations(data.variations || []);
      setShopResults(data.shopResults || []);
      setStarCount(data.blueprint?.starCount || 0);
      setForkBasedOn(data.blueprint?.blueprintId || '');
      setSaved(data.isSaved || false);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="workshop-page"><Header /><p style={{padding:'8rem 2rem',textAlign:'center'}}>Loading...</p></div>;
  if (!bp) return <div className="workshop-page"><Header /><p style={{padding:'8rem 2rem',textAlign:'center'}}>Blueprint not found.</p></div>;

  const tier = customer?.subscriptionTier || '';
  const isVendor = vendor?.vendorStatus === 'active';
  const hasAccess = isVendor || tier === 'insider';
  if (!hasAccess) return (
    <div className="workshop-page"><Header />
      <div className="workshop-content" style={{textAlign:'center', padding:'8rem 2rem'}}>
        <h1>🔒 Insider Access Only</h1>
        <p style={{color:'var(--text-muted)', margin:'1rem 0 2rem'}}>The Workshop is exclusively available to Insider subscribers and registered vendors.</p>
        <Link to="/subscribe" className="btn-primary" style={{textDecoration:'none'}}>Upgrade to Insider — $12.99/mo</Link>
      </div>
    </div>
  );

  const latestVersion = versions.length > 0 ? versions[versions.length - 1] : {};
  const safeArray = (val) => { if (Array.isArray(val)) return val; try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } };
  const cutList = safeArray(latestVersion.cutList);
  const steps = safeArray(latestVersion.steps);
  const tools = safeArray(latestVersion.tools);
  const userId = customer?.customerId || vendor?.vendorId;
  const isOwner = bp.creatorId === userId;
  const hasPurchased = isOwner || bp.price === 0 || (bp.purchasers || []).includes(userId);

  // "Based on" options: current bp + all variations with same original
  const forkOptions = [
    { id: bp.blueprintId, label: `${bp.title} (${bp.forkedFrom ? 'this variation' : 'original'})` },
    ...variations.filter(v => v.status === 'published').map(v => ({ id: v.blueprintId, label: `${v.title} by ${v.creatorName || v.creatorId}` }))
  ];

  const handleSave = async () => {
    try {
      if (saved) { await unsaveBlueprintApi(bp.blueprintId); setSaved(false); setStarCount(s => s - 1); }
      else { await saveBlueprintApi(bp.blueprintId); setSaved(true); setStarCount(s => s + 1); }
    } catch (e) { /* silent */ }
  };

  return (
    <div className="workshop-page">
      <Header />
      <div className="workshop-content">
        <Link to="/workshop/blueprints" className="bp-back">← Back to Workshop</Link>

        <div className="bp-detail-header">
          <div>
            <h1>{bp.title}</h1>
            {bp.forkedFrom && <p className="bp-based-on">Based on another blueprint • <Link to={`/workshop/blueprints/${bp.forkedFrom}`}>View original</Link></p>}
            <p className="bp-detail-desc">{bp.description}</p>
          </div>
          <div className="bp-detail-sidebar">
            <div className="bp-detail-meta">
              <span>⏱ ~{bp.estimatedHours} hours</span>
              <span>📏 {bp.difficulty}</span>
              <span>💰 ~${bp.estimatedCost} materials</span>
              <span>v{bp.currentVersion}</span>
            </div>
            <div className="bp-detail-actions">
              {bp.price > 0 && !hasPurchased ? (
                <button className="btn-primary" onClick={async () => {
                  try {
                    const data = await purchaseBlueprint(bp.blueprintId);
                    if (data.url) window.location.href = data.url;
                    else window.location.reload();
                  } catch (err) { alert(err.error || 'Purchase failed'); }
                }}>Purchase Plan — ${(bp.price / 100).toFixed(2)}</button>
              ) : (
                <button className="btn-primary" disabled={!hasPurchased}>View Full Plan{bp.price > 0 ? ` — $${(bp.price / 100).toFixed(2)}` : ' — Free'}</button>
              )}
              {(customer || vendor) && hasPurchased && <button className="btn-secondary" onClick={() => setShowFork(true)}>✂️ Make Your Version</button>}
              {(customer || vendor) && !hasPurchased && bp.price > 0 && <button className="btn-secondary" disabled>🔒 Purchase to fork</button>}
              {(customer || vendor) && (
                <button className={`btn-secondary ${saved ? 'saved' : ''}`} onClick={handleSave}>
                  {saved ? '★' : '☆'} Save ({starCount})
                </button>
              )}
              {!(customer || vendor) && <button className="btn-secondary" disabled>★ Save ({starCount})</button>}
            </div>
            <span className={`bp-license-badge ${bp.license}`}>{bp.license === 'mit' ? '🟢 Open — Variations can be sold' : bp.license === 'cc-by' ? '🟡 Attribution required' : '🔒 Protected — Personal use only'}</span>
          </div>
        </div>

        <div className="bp-tabs">
          <button className={`tab ${activeTab === 'cutlist' ? 'active' : ''}`} onClick={() => setActiveTab('cutlist')}>Materials</button>
          <button className={`tab ${activeTab === 'steps' ? 'active' : ''}`} onClick={() => setActiveTab('steps')}>Steps ({steps.length})</button>
          <button className={`tab ${activeTab === 'versions' ? 'active' : ''}`} onClick={() => setActiveTab('versions')}>Revisions ({versions.length})</button>
          <button className={`tab ${activeTab === 'variations' ? 'active' : ''}`} onClick={() => setActiveTab('variations')}>Variations ({variations.length})</button>
          <button className={`tab ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>Shop Results ({shopResults.length})</button>
        </div>

        <div className="bp-tab-content">
          {activeTab === 'cutlist' && (
            <div>
              <table className="bp-cut-list">
                <thead><tr><th>Qty</th><th>Material</th><th>Dimensions</th><th>Notes</th></tr></thead>
                <tbody>
                  {cutList.map((item, i) => (
                    <tr key={i}><td>{item.qty}</td><td>{item.material}</td><td>{item.dimensions}</td><td>{item.notes}</td></tr>
                  ))}
                </tbody>
              </table>
              {cutList.length === 0 && <p className="empty-state">No materials list added yet.</p>}
              <h3 className="bp-section-title">Tools & Equipment</h3>
              <div className="bp-tags">{tools.map(t => <span className="bp-tag" key={t}>{t}</span>)}</div>
            </div>
          )}

          {activeTab === 'steps' && (
            <div className="bp-steps">
              {steps.map((step, i) => (
                <div className="bp-step" key={i}>
                  <div className="bp-step-num">{i + 1}</div>
                  <div className="bp-step-content">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
              {steps.length === 0 && <p className="empty-state">No steps added yet.</p>}
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="bp-versions">
              {versions.map((v, i) => (
                <div className="bp-version-item" key={i}>
                  <span className="bp-version-tag">v{v.version}</span>
                  <div>
                    <p>{v.changelog}</p>
                    <span className="bp-version-date">{new Date((v.publishedAt || 0) * 1000).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'variations' && (
            <div className="bp-variations">
              <h3 className="bp-section-title">Provenance Graph</h3>
              {bp.forkedFrom && (
                <p style={{fontSize:'0.8rem',marginBottom:'1rem'}}>
                  You're viewing a variation. <Link to={`/workshop/blueprints/${bp.originalBlueprintId || bp.forkedFrom}`} style={{color:'var(--accent)'}}>← View full graph on original</Link>
                </p>
              )}
              {(mainlineVersions.length > 0 || variations.length > 0) ? (
                <FamilyTree versions={mainlineVersions} variations={variations} currentBpId={id} navigate={navigate} />
              ) : (
                <p className="empty-state">No history yet.</p>
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div className="bp-results">
              {shopResults.length === 0 && <p className="empty-state">No shop results yet. Build this and post your result!</p>}
              {shopResults.map((r, i) => (
                <div className="bp-result-item" key={i}>
                  <div className="bp-result-header">
                    <h4>Shop result by {r.displayName || r.userId}</h4>
                    <span>{new Date((r.createdAt || 0) * 1000).toLocaleDateString()}</span>
                  </div>
                  <p>{r.notes}</p>
                </div>
              ))}
              {(customer || vendor) && <button className="btn-primary" style={{marginTop:'1.5rem'}} onClick={() => setShowResult(true)}>+ Post Your Shop Result</button>}
            </div>
          )}
        </div>

        {/* Fork Modal with "Based on" selector */}
        {showFork && (
          <div className="item-form-overlay">
            <div className="item-form">
              <h3>✂️ Make Your Version</h3>
              <p className="settings-hint">This creates a variation. Attribution to the original is permanent.</p>
              <label style={{fontSize:'0.8rem',color:'var(--text-muted)',marginBottom:'0.25rem',display:'block'}}>Based on:</label>
              <select value={forkBasedOn} onChange={e => setForkBasedOn(e.target.value)} style={{width:'100%',marginBottom:'1rem',padding:'0.5rem',borderRadius:'var(--radius)',border:'1px solid rgba(200,164,92,0.2)',background:'var(--surface)',color:'var(--text)'}}>
                {forkOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
              <input type="text" placeholder="Your variation title *" value={forkTitle} onChange={e => setForkTitle(e.target.value)} />
              <div className="form-buttons">
                <button className="btn-primary" onClick={async () => {
                  if (!forkTitle.trim()) { alert('Title required'); return; }
                  try {
                    const data = await forkBlueprint(forkBasedOn, { title: forkTitle, forkedFrom: forkBasedOn });
                    navigate(`/workshop/blueprints/${data.blueprintId}`);
                  } catch (err) { alert(err.error || 'Failed to create variation'); }
                }}>Create Variation</button>
                <button className="btn-secondary" onClick={() => setShowFork(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showResult && (
          <div className="item-form-overlay">
            <div className="item-form">
              <h3>🔨 Post Your Shop Result</h3>
              <p className="settings-hint">Share photos and notes from your build of this blueprint.</p>
              <input type="text" placeholder="Your name / shop name" value={resultForm.displayName} onChange={e => setResultForm({...resultForm, displayName: e.target.value})} />
              <textarea placeholder="How did your build go? Tips, changes, experience..." value={resultForm.notes} onChange={e => setResultForm({...resultForm, notes: e.target.value})} rows={4} />
              <div className="form-buttons">
                <button className="btn-primary" onClick={async () => {
                  try {
                    await postShopResult(bp.blueprintId, resultForm);
                    setShowResult(false);
                    setResultForm({ displayName: '', notes: '' });
                    const data = await getBlueprint(id);
                    setShopResults(data.shopResults || []);
                  } catch (err) { alert(err.error || 'Failed to post'); }
                }}>Post Result</button>
                <button className="btn-secondary" onClick={() => setShowResult(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlueprintDetail;
